import { LIBRARIES, LITERALS, RESERVED_WORDS, BUILT_INS, ROBLOX_BUILTINS } from './luau-constants';

type libraryDefinition = {
  className: 'built_in';
  begin: string;
  end: string;
  contains: { begin: '\\.'; className: 'punctuation' }[];
};

export default function luau(hljs?: HLJSApi): Language {
  if (!hljs) {
    throw new Error('Highlight.js not found');
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const KEYWORDS: Record<string, any> = {
    $pattern: hljs.UNDERSCORE_IDENT_RE,
    literal: LITERALS,
    keyword: RESERVED_WORDS,
    built_in: BUILT_INS.concat(ROBLOX_BUILTINS),
  };

  // Build definitions for library methods and properties
  const LIBRARY_DEFS: libraryDefinition[] = [];
  Object.entries(LIBRARIES).forEach(([library, methods]) => {
    LIBRARY_DEFS.push({
      className: 'built_in',
      begin: `\\b${library}(?=\\.)`,
      end: `${methods.join('|')}\\b`,
      contains: [
        {
          begin: '\\.',
          className: 'punctuation',
        },
      ],
    });
  });

  const OPENING_LONG_BRACKET = '\\[=*\\[';
  const CLOSING_LONG_BRACKET = '\\]=*\\]';
  const LONG_BRACKETS: Mode = {
    begin: OPENING_LONG_BRACKET,
    end: CLOSING_LONG_BRACKET,
    contains: ['self'],
  };

  const COMMENT: Mode = hljs.COMMENT(`--(?!${OPENING_LONG_BRACKET})`, '$');
  const MULTI_LINE_COMMENT: Mode = hljs.COMMENT(`--${OPENING_LONG_BRACKET}`, CLOSING_LONG_BRACKET, {
    contains: [LONG_BRACKETS],
    relevance: 10,
  });

  const FUNCTION: Mode = {
    className: 'function',
    beginKeywords: 'function',
    end: '\\)',
    contains: [
      hljs.inherit(hljs.TITLE_MODE, {
        begin: '([_a-zA-Z]\\w*\\.)*([_a-zA-Z]\\w*:)?[_a-zA-Z]\\w*',
      }),
      {
        className: 'params',
        begin: '\\(',
        endsWithParent: true,
        contains: [COMMENT, MULTI_LINE_COMMENT],
      },
      COMMENT,
      MULTI_LINE_COMMENT,
    ],
  };

  const SUBST: Mode = {
    className: 'subst',
    begin: /\{/,
    end: /\}/,
    keywords: KEYWORDS,
    illegal: /--/,
    contains: [
      MULTI_LINE_COMMENT,
      hljs.C_NUMBER_MODE,
      hljs.APOS_STRING_MODE,
      hljs.QUOTE_STRING_MODE,
      'self',
      ...LIBRARY_DEFS,
    ],
  };

  const STRING: Mode = {
    className: 'string',
    contains: [hljs.BACKSLASH_ESCAPE],
    variants: [
      hljs.APOS_STRING_MODE,
      hljs.QUOTE_STRING_MODE,
      {
        begin: OPENING_LONG_BRACKET,
        end: CLOSING_LONG_BRACKET,
        contains: [hljs.BACKSLASH_ESCAPE],
      },
      {
        begin: /`/,
        end: /`/,
        contains: [hljs.BACKSLASH_ESCAPE, SUBST],
        relevance: 5,
      },
    ],
  };

  return {
    name: 'Luau',
    aliases: ['luau'],
    keywords: KEYWORDS,
    contains: [COMMENT, MULTI_LINE_COMMENT, hljs.C_NUMBER_MODE, STRING, FUNCTION, ...LIBRARY_DEFS],
  };
}
